import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ReactNode } from "react";

interface ShipmentInfoColumnProps {
  label: ReactNode;
  value: ReactNode;
  /**
   * `end` for the shipment top bar, whose block is right-aligned against the
   * page edge; `start` (the default) everywhere inside a section body.
   */
  align?: "start" | "end" | undefined;
  /**
   * Keep both lines unbroken. The top bar needs it — a value like
   * "Tomorrow, 10am-4:30pm" reads wrong broken in half, and an unbreakable
   * block is what stops the bar being squeezed (ENG-5706). Inside a section
   * body, where a long date has room to wrap, leave it off.
   */
  nowrap?: boolean | undefined;
}

/**
 * One label-over-value column of shipment data — BOL number, status, a claim's
 * submitted date.
 *
 * Both lines are crisp `default`: the rubric puts a key-value label and a
 * field's headline value there, and this is the page's primary operational
 * data. The label reads as a label from its weight, not from being dimmed —
 * which is the rubric's explicit instruction. Don't reintroduce a colour here.
 *
 * It is shared rather than copied because the copies drifted: the claims card
 * had grown its own smaller, dimmer version, and the mismatch against the top
 * bar directly above it is what ENG-6743 was raised about.
 */
export function ShipmentInfoColumn(props: ShipmentInfoColumnProps) {
  // Defaulted rather than forwarded as `undefined`: T types `nowrap` as a plain
  // boolean, and false renders exactly what omitting it does.
  const nowrap = props.nowrap ?? false;

  return (
    <Stack gap={0} align={props.align ?? "start"}>
      <T size="base" weight="light" nowrap={nowrap}>
        {props.label}
      </T>
      <T size="base" weight="semibold" nowrap={nowrap}>
        {props.value}
      </T>
    </Stack>
  );
}
